import {
  Button,
  EmptyState,
  FilterSegments,
  PageHeader,
  PageTitle,
  RowActions,
  SearchInput,
  StatusTag,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
  titleCase,
  type FilterSegmentOption,
} from '@westhetics/ui'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { ListFooter } from '@/app/list-footer'
import { LoadError } from '@/app/load-error'
import { Page } from '@/app/shell'
import { useSession } from '@/lib/session'
import { useDebounced } from '@/lib/use-debounced'
import { useStoredFilter } from '@/lib/use-stored-filter'
import { routes } from '@/routes'
import { useUsers, type UserFilter } from './api'
import { REMOVE_ANCHOR, ROLE_LABELS, userStatus } from './labels'

/**
 * Usuários da clínica (docs/areas/administracao.md, "Usuários"; RN-ADM-16). Arquétipo: lista de
 * trabalho, de gestora e de root com clínica ativa. Cada linha é um vínculo da pessoa com esta
 * clínica; root e vínculos removidos não aparecem.
 */

const FILTERS: UserFilter[] = ['active', 'blocked']

export function UsersPage() {
  const session = useSession()
  const navigate = useNavigate()

  const { filter, setFilter } = useStoredFilter('users', FILTERS, 'active')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const debouncedSearch = useDebounced(search.trim())
  const query = useUsers({ filter, search: debouncedSearch || undefined, page })
  const counts = query.data?.meta.counts
  const total = (counts?.active ?? 0) + (counts?.blocked ?? 0)

  const options: FilterSegmentOption<UserFilter>[] = [
    { value: 'active', label: 'Ativos', count: counts?.active },
    { value: 'blocked', label: 'Bloqueados', count: counts?.blocked },
  ]

  const users = query.data?.data ?? []
  const hasNoUser = !query.isPending && total === 0
  const showSearch = total > 20 || search !== ''

  return (
    <>
      <PageHeader
        crumbs={[{ label: 'Administração' }, { label: 'Usuários' }]}
        actions={
          <Button size="sm" asChild>
            <Link to={routes.admin.newUser}>+ Criar usuário</Link>
          </Button>
        }
      />
      <Page wide>
        <PageTitle
          title="Usuários"
          subtitle="Quem entra no sistema. Prestador que aplica procedimento é cadastrado em Prestadores."
        />

        {query.isError ? (
          <LoadError
            className="mt-6"
            title="Não conseguimos carregar os usuários"
            error={query.error}
            onRetry={() => void query.refetch()}
          />
        ) : hasNoUser ? (
          <EmptyState
            className="mt-6"
            title="Nenhum usuário ainda"
            description="Usuário é quem entra no sistema desta clínica: gestora, recepção ou prestador com login. Cada pessoa recebe o convite por e-mail e define a própria senha."
            action={
              <Button asChild>
                <Link to={routes.admin.newUser}>Criar o primeiro usuário</Link>
              </Button>
            }
          />
        ) : (
          <>
            <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
              <FilterSegments
                label="Filtrar usuários"
                options={options}
                value={filter}
                onChange={(value) => {
                  setFilter(value)
                  setPage(1)
                }}
              />
              {showSearch ? (
                <SearchInput
                  className="tablet:max-w-[300px]"
                  value={search}
                  onChange={(value) => {
                    setSearch(value)
                    setPage(1)
                  }}
                  placeholder="Buscar por nome ou e-mail"
                  aria-label="Buscar usuário"
                />
              ) : null}
            </div>

            <div className="mt-4">
              {query.isPending ? (
                <TableSkeleton rows={4} columns={5} />
              ) : users.length === 0 ? (
                <EmptyState
                  title={
                    debouncedSearch
                      ? `Nenhum usuário para "${debouncedSearch}"`
                      : filter === 'blocked'
                        ? 'Nenhum usuário bloqueado'
                        : 'Nenhum usuário ativo'
                  }
                  description={
                    debouncedSearch
                      ? 'A busca cobre o nome e o e-mail, dentro do filtro escolhido.'
                      : filter === 'blocked'
                        ? 'Bloquear impede a entrada nesta clínica sem apagar nada do que a pessoa fez. Quem for bloqueado aparece aqui e pode ser liberado a qualquer momento.'
                        : 'Todos os usuários desta clínica estão com o acesso bloqueado. Libere alguém ou crie um usuário.'
                  }
                  action={
                    debouncedSearch ? (
                      <Button variant="outline" onClick={() => setSearch('')}>
                        Limpar busca
                      </Button>
                    ) : filter === 'blocked' ? (
                      <Button variant="outline" onClick={() => setFilter('active')}>
                        Ver ativos
                      </Button>
                    ) : (
                      <Button variant="outline" onClick={() => setFilter('blocked')}>
                        Ver bloqueados
                      </Button>
                    )
                  }
                />
              ) : (
                <Table>
                  <TableHeader>
                    <tr>
                      <TableHead>Nome</TableHead>
                      <TableHead>E-mail</TableHead>
                      <TableHead>Permissão</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead aria-label="Ações" />
                    </tr>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => {
                      const status = userStatus(user)
                      // Ninguém remove a própria conta (RN-ADM-13): a ação some da própria linha.
                      const isSelf = user.userId === session.profile?.userId
                      return (
                        <TableRow
                          key={user.id}
                          onClick={() => navigate(routes.admin.user(user.id))}
                        >
                          <TableCell primary>{titleCase(user.name)}</TableCell>
                          <TableCell label="E-mail" muted className="break-all">
                            {user.email}
                          </TableCell>
                          <TableCell label="Permissão">{ROLE_LABELS[user.role]}</TableCell>
                          <TableCell label="Status">
                            <StatusTag level={status.level}>{status.label}</StatusTag>
                          </TableCell>
                          <TableCell>
                            <RowActions>
                              <Link to={routes.admin.user(user.id)}>editar</Link>
                              {isSelf ? null : (
                                <Link
                                  to={{ pathname: routes.admin.user(user.id), hash: REMOVE_ANCHOR }}
                                >
                                  remover
                                </Link>
                              )}
                            </RowActions>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              )}
              {query.data && users.length > 0 ? (
                <ListFooter
                  meta={query.data.meta}
                  noun={['usuário', 'usuários']}
                  note="em ordem alfabética"
                  onPage={setPage}
                />
              ) : null}
            </div>
          </>
        )}
      </Page>
    </>
  )
}
